import {
  PERSON_ROLE_LEARNER,
  PERSON_ROLE_STAFF,
} from './case-plan-create.constants';
import { getResidentDisplayName } from './case-plan-create-person.util';

/**
 * Returns the first non-empty string from the given values.
 * @param {...*} values
 * @returns {string}
 */
const firstNonEmpty = (...values) => {
  const match = values.find((value) => Boolean(value));
  return match ? String(match) : '';
};

/**
 * Builds a display name from first/last/full name fields.
 * @param {object} person
 * @returns {string}
 */
const buildPersonDisplayName = (person) => {
  if (!person) {
    return '';
  }

  if (person.fullName) {
    return person.fullName;
  }

  if (person.name) {
    return person.name;
  }

  return `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim();
};

/**
 * Normalizes a resident search API item into the Create Plan UI shape.
 * API uses hasExistingPlan / userName; the form uses hasPlan / username.
 * @param {object} item
 * @returns {object|null}
 */
export const mapResidentSearchItem = (item) => {
  if (!item || item.personId == null) {
    return null;
  }

  return {
    personId: item.personId,
    firstName: item.firstName ?? '',
    lastName: item.lastName ?? '',
    username: item.userName ?? item.username ?? '',
    docId: item.docId ?? null,
    location: item.location ?? item.facilityName ?? '',
    hasPlan: Boolean(item.hasExistingPlan ?? item.hasPlan),
  };
};

/**
 * Normalizes a staff search API item into the Create Plan editor shape.
 * @param {object} item
 * @returns {object|null}
 */
export const mapEditorSearchItem = (item) => {
  if (!item || item.personId == null) {
    return null;
  }

  const displayName = buildPersonDisplayName(item);

  return {
    personId: item.personId,
    name: firstNonEmpty(displayName, item.username),
    username: item.username ?? '',
    role: item.roleName ?? item.role ?? PERSON_ROLE_STAFF,
  };
};

/**
 * Normalizes a template list API item to the dropdown option shape.
 * @param {object} item
 * @returns {object|null}
 */
export const mapTemplateListItem = (item) => {
  if (!item?.id) {
    return null;
  }

  return {
    id: item.id,
    name: item.name ?? '',
  };
};

/**
 * Maps a resident search result into the shape expected by Attorney AutocompleteOption.
 * @param {object} resident
 * @returns {object|null}
 */
export const mapResidentToAutocompleteOption = (resident) => {
  if (!resident) {
    return null;
  }

  const name = getResidentDisplayName(resident);

  return {
    ...resident,
    name,
    label: name,
    role: PERSON_ROLE_LEARNER,
    roleName: PERSON_ROLE_LEARNER,
    facilityName: resident.facilityName ?? resident.location ?? '',
  };
};

/**
 * Maps an editor search result into the shape expected by MemberAutocomplete options.
 * @param {object} editor
 * @returns {object|null}
 */
export const mapEditorToAutocompleteOption = (editor) => {
  if (!editor) {
    return null;
  }

  const name = editor.name ?? editor.username ?? '';
  const role = editor.role ?? PERSON_ROLE_STAFF;

  return {
    ...editor,
    name,
    label: name,
    role,
    roleName: role,
  };
};

/**
 * Passes options straight through the Autocomplete filter.
 * Server-side search already returns matched results, so no client filtering.
 * @param {object[]} options
 * @returns {object[]}
 */
export const passthroughFilterOptions = (options) => options;

/**
 * Stable match key for person equality checks (personId preferred, else username).
 * @param {object|null|undefined} person
 * @returns {string|null}
 */
const getPersonMatchKey = (person) => {
  if (person?.personId != null) {
    return `id:${person.personId}`;
  }

  if (person?.username) {
    return `user:${person.username}`;
  }

  return null;
};

/**
 * Whether a person option matches any person in the given selection.
 * @param {object} option
 * @param {object[]} selection
 * @returns {boolean}
 */
export const isPersonInSelection = (option, selection) =>
  (selection ?? []).some(
    (person) => getPersonMatchKey(person) === getPersonMatchKey(option),
  );

/**
 * Equality check used by MemberAutocomplete for residents/editors.
 * @param {object} option
 * @param {object} selected
 * @returns {boolean}
 */
export const isPersonOptionEqual = (option, selected) => {
  const optionKey = getPersonMatchKey(option);
  const selectedKey = getPersonMatchKey(selected);
  return Boolean(optionKey && selectedKey && optionKey === selectedKey);
};
